import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Pencil, Trash2, Check, X, Tag, Ruler } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

// ── ABM genérico inline ───────────────────────────────────────────────────
function ItemRow({ item, onSave, onDelete, canEdit, extraField }) {
  const [editing, setEditing]   = useState(false);
  const [nombre,  setNombre]    = useState(item.nombre);
  const [extra,   setExtra]     = useState(item[extraField?.key] ?? '');
  const [saving,  setSaving]    = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const cancel = () => {
    setNombre(item.nombre);
    setExtra(item[extraField?.key] ?? '');
    setEditing(false);
  };

  const save = async () => {
    if (!nombre.trim()) { toast.error('El nombre es requerido'); return; }
    if (extraField && !extra.trim()) { toast.error(`${extraField.label} es requerido`); return; }
    setSaving(true);
    await onSave(item.id, nombre.trim(), extra.trim());
    setSaving(false);
    setEditing(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') cancel();
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 py-2 px-3 rounded-xl bg-primary-50 dark:bg-primary-500/10 ring-1 ring-primary-200 dark:ring-primary-500/30">
        <input
          ref={inputRef}
          className="input py-1.5 text-sm flex-1"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Nombre"
        />
        {extraField && (
          <input
            className="input py-1.5 text-sm w-20"
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            onKeyDown={handleKey}
            placeholder={extraField.placeholder}
            maxLength={10}
          />
        )}
        <button
          onClick={save}
          disabled={saving}
          className="p-1.5 rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors shrink-0 disabled:opacity-50"
        >
          <Check size={14} />
        </button>
        <button
          onClick={cancel}
          className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors shrink-0"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 py-2 px-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 group transition-colors">
      <span className="flex-1 text-[13px] font-medium text-slate-700 dark:text-slate-300">{item.nombre}</span>
      {extraField && (
        <span className="text-[12px] font-mono text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
          {item[extraField.key]}
        </span>
      )}
      {canEdit && (
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button
            onClick={() => setEditing(true)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={() => onDelete(item.id, item.nombre)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Fila de nuevo ítem ────────────────────────────────────────────────────
function AddRow({ onAdd, placeholder, extraField }) {
  const [nombre,  setNombre]  = useState('');
  const [extra,   setExtra]   = useState('');
  const [saving,  setSaving]  = useState(false);

  const submit = async () => {
    if (!nombre.trim()) { toast.error('El nombre es requerido'); return; }
    if (extraField && !extra.trim()) { toast.error(`${extraField.label} es requerido`); return; }
    setSaving(true);
    const ok = await onAdd(nombre.trim(), extra.trim());
    setSaving(false);
    if (ok) { setNombre(''); setExtra(''); }
  };

  const handleKey = (e) => { if (e.key === 'Enter') submit(); };

  return (
    <div className="flex items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-800 mt-1">
      <input
        className="input py-2 text-sm flex-1"
        placeholder={placeholder}
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        onKeyDown={handleKey}
      />
      {extraField && (
        <input
          className="input py-2 text-sm w-20"
          placeholder={extraField.placeholder}
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          onKeyDown={handleKey}
          maxLength={10}
        />
      )}
      <button
        onClick={submit}
        disabled={saving}
        className="btn-primary py-2 shrink-0 disabled:opacity-50"
      >
        <Plus size={15} />
        {saving ? '...' : 'Agregar'}
      </button>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────
export default function Categorias() {
  const { can } = useAuth();

  const [categorias,  setCategorias]  = useState([]);
  const [unidades,    setUnidades]    = useState([]);
  const [loadingCat,  setLoadingCat]  = useState(true);
  const [loadingUni,  setLoadingUni]  = useState(true);

  const [confirmId,    setConfirmId]    = useState(null);
  const [confirmTipo,  setConfirmTipo]  = useState(null);
  const [confirmNombre, setConfirmNombre] = useState('');
  const [deleting,     setDeleting]     = useState(false);

  const canEdit = can('configuracion.editar');

  // ── Fetch ──
  const fetchCategorias = useCallback(async () => {
    setLoadingCat(true);
    try { setCategorias((await api.get('/categorias/index.php')).data); }
    catch { toast.error('Error al cargar categorías'); }
    finally { setLoadingCat(false); }
  }, []);

  const fetchUnidades = useCallback(async () => {
    setLoadingUni(true);
    try { setUnidades((await api.get('/unidades/index.php')).data); }
    catch { toast.error('Error al cargar unidades'); }
    finally { setLoadingUni(false); }
  }, []);

  useEffect(() => { fetchCategorias(); fetchUnidades(); }, [fetchCategorias, fetchUnidades]);

  // ── Categorías: add / save / delete ──
  const addCategoria = async (nombre) => {
    try {
      const res = await api.post('/categorias/index.php', { nombre });
      setCategorias(prev => [...prev, res.data].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      toast.success('Categoría creada');
      return true;
    } catch (err) { toast.error(err?.message || 'Error'); return false; }
  };

  const saveCategoria = async (id, nombre) => {
    try {
      await api.put(`/categorias/index.php?id=${id}`, { nombre });
      setCategorias(prev => prev.map(c => c.id === id ? { ...c, nombre } : c).sort((a, b) => a.nombre.localeCompare(b.nombre)));
      toast.success('Categoría actualizada');
    } catch (err) { toast.error(err?.message || 'Error'); }
  };

  // ── Unidades: add / save / delete ──
  const addUnidad = async (nombre, simbolo) => {
    try {
      const res = await api.post('/unidades/index.php', { nombre, simbolo });
      setUnidades(prev => [...prev, res.data].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      toast.success('Unidad creada');
      return true;
    } catch (err) { toast.error(err?.message || 'Error'); return false; }
  };

  const saveUnidad = async (id, nombre, simbolo) => {
    try {
      await api.put(`/unidades/index.php?id=${id}`, { nombre, simbolo });
      setUnidades(prev => prev.map(u => u.id === id ? { ...u, nombre, simbolo } : u).sort((a, b) => a.nombre.localeCompare(b.nombre)));
      toast.success('Unidad actualizada');
    } catch (err) { toast.error(err?.message || 'Error'); }
  };

  // ── Delete genérico ──
  const askDelete = (id, nombre, tipo) => {
    setConfirmId(id);
    setConfirmTipo(tipo);
    setConfirmNombre(nombre);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (confirmTipo === 'categoria') {
        await api.delete(`/categorias/index.php?id=${confirmId}`);
        setCategorias(prev => prev.filter(c => c.id !== confirmId));
        toast.success('Categoría eliminada');
      } else {
        await api.delete(`/unidades/index.php?id=${confirmId}`);
        setUnidades(prev => prev.filter(u => u.id !== confirmId));
        toast.success('Unidad eliminada');
      }
      setConfirmId(null);
    } catch (err) {
      toast.error(err?.message || 'Error al eliminar');
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-5">
      <div className="page-header">
        <h1 className="page-title">Categorías y unidades</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* ── Categorías ── */}
        <div className="card dark:bg-slate-800">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center">
              <Tag size={15} className="text-primary-500 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900 dark:text-white">Categorías</h2>
              <p className="text-[11px] text-slate-400">Clasificación de productos</p>
            </div>
            <span className="ml-auto badge-neutral">{categorias.length}</span>
          </div>

          {loadingCat ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-0.5 min-h-[120px]">
              {categorias.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-6">Sin categorías aún</p>
              )}
              {categorias.map(c => (
                <ItemRow
                  key={c.id}
                  item={c}
                  canEdit={canEdit}
                  onSave={saveCategoria}
                  onDelete={(id, nombre) => askDelete(id, nombre, 'categoria')}
                />
              ))}
            </div>
          )}

          {canEdit && (
            <AddRow
              onAdd={addCategoria}
              placeholder="Nueva categoría..."
            />
          )}
        </div>

        {/* ── Unidades ── */}
        <div className="card dark:bg-slate-800">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
              <Ruler size={15} className="text-emerald-500 dark:text-emerald-400" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900 dark:text-white">Unidades de medida</h2>
              <p className="text-[11px] text-slate-400">Unidades usadas en productos</p>
            </div>
            <span className="ml-auto badge-neutral">{unidades.length}</span>
          </div>

          {loadingUni ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-0.5 min-h-[120px]">
              {unidades.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-6">Sin unidades aún</p>
              )}
              {unidades.map(u => (
                <ItemRow
                  key={u.id}
                  item={u}
                  canEdit={canEdit}
                  onSave={saveUnidad}
                  onDelete={(id, nombre) => askDelete(id, nombre, 'unidad')}
                  extraField={{ key: 'simbolo', label: 'Símbolo', placeholder: 'kg' }}
                />
              ))}
            </div>
          )}

          {canEdit && (
            <AddRow
              onAdd={addUnidad}
              placeholder="Nueva unidad..."
              extraField={{ key: 'simbolo', label: 'Símbolo', placeholder: 'kg' }}
            />
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title={`Eliminar ${confirmTipo === 'categoria' ? 'categoría' : 'unidad'}`}
        message={`¿Eliminar "${confirmNombre}"? No podrás hacerlo si hay productos que la usan.`}
      />
    </div>
  );
}
